import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormRow, Pagination, Select } from '~/components';
import {
  MarketEmptyPanel,
  MarketSelectableCard,
  MarketStatusPill,
} from '~/features/shared/market-content';
import { SearchFit } from '~/features/shared/market-content/SearchFit';
import type { SearchFitResult } from '~/features/shared/market-content/useSearchFit';
import { OptionType } from '~/features/types';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import { useListingProviderCategories } from '../ProviderCategoriesContext';
import { SORT_BY_OPTIONS } from '../consts';
import {
  selectSearchEmployerUser,
  selectSearchListing,
  selectSearchListings,
  selectSearchPagination,
} from '../selectors';
import { Context } from '../types';
import { ListingCardFacts } from './ListingCardParts';

const TAGS = CT.listingSearch.list;

export const ListingsList = ({
  fit,
  onSelectListing,
}: {
  fit?: SearchFitResult;
  /** Called after a row is picked; the narrow layout uses it to open the detail pane. */
  onSelectListing?: () => void;
}) => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const categories = useListingProviderCategories()?.categories ?? [];
  const listings = useSelector(msaasListingsSearchRef, selectSearchListings);
  const currentListing = useSelector(msaasListingsSearchRef, selectSearchListing);
  const pagination = useSelector(msaasListingsSearchRef, selectSearchPagination);
  const employerUser = useSelector(msaasListingsSearchRef, selectSearchEmployerUser);
  if (!msaasListingsSearchRef || !pagination) return null;

  const onSortChange = (e: unknown) => {
    console.log('onSortChange', e);
    msaasListingsSearchRef.send({
      type: 'ON_SORT',
      payload: {
        sortBy: (e as OptionType).value as Context['sortBy'],
      },
    });
  };

  return (
    <div className="px-4 pt-4 text-left">
      <div className="pb-3 mb-4 border-b border-grey-900 text-left">
        <h3 className="text-xl font-bold leading-snug text-content-primary w-full mb-3">
          B2B Listings
        </h3>
        <p className="text-sm font-normal leading-6 text-content-secondary">
          {pagination.total} listings found
        </p>
        <FormRow className="w-full mt-3">
          <Select
            name={'sortBy'}
            label={'Sort By'}
            cyTag={TAGS.sortBy}
            options={SORT_BY_OPTIONS}
            onChange={onSortChange}
          />
        </FormRow>
      </div>
      <div>
        {listings.length === 0 && (
          <MarketEmptyPanel title="No listings found" className="min-h-[140px]" />
        )}
        {listings.map((listing, idx) => {
          const hasResponded = listing.jobResponses?.some(
            (r) => r.respondingEmployerId === employerUser?.employerId
          );
          const isSelected = listing.id === currentListing?.id;
          return (
            <MarketSelectableCard
              key={listing.id}
              selected={isSelected}
              dataCy={TAGS.card + idx}
              className="mb-3"
              onClick={() => {
                msaasListingsSearchRef.send({
                  type: 'ON_SELECT',
                  payload: { listingId: listing.id },
                });
                onSelectListing?.();
              }}
            >
              <div className="mb-2 space-y-1">
                <p className="text-lg font-bold leading-snug text-content-primary [overflow-wrap:anywhere]">
                  {listing.listingTitle}
                </p>
                <div className="flex flex-wrap gap-2">
                  {hasResponded && <MarketStatusPill status="responded" label="Responded" />}
                  {listing.isRenewable && <MarketStatusPill status="neutral" label="Renewable" />}
                </div>
              </div>
              {/* [MCV-1961] No attached providers means no match to show; the detail panel keeps
                  its explanation. Loading, scores and errors still render. */}
              {fit && !(fit.scores[listing.id]?.status === 'NO_PROVIDERS' && !fit.error) && (
                <SearchFit
                  kind="b2b"
                  summary={fit.scores[listing.id]}
                  loading={fit.loading}
                  error={fit.error}
                />
              )}
              <ListingCardFacts
                listing={listing}
                categories={categories}
                cyTags={{
                  states: TAGS.states + idx,
                  ftes: TAGS.ftesRequired + idx,
                  dates: TAGS.startDate + idx,
                  workLocation: TAGS.workLocation + idx,
                  contractValue: TAGS.contractValue + idx,
                }}
              />
            </MarketSelectableCard>
          );
        })}
        <Pagination
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.pageSize)}
          onPageChange={(page) => {
            msaasListingsSearchRef.send({ type: 'ON_PAGINATE', page });
          }}
        />
      </div>
    </div>
  );
};
